import {
  COMPLIANCE_MESSAGE_DEFAULT,
  hasSubscription,
} from "@markit/common.utils";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { ContactCardFirstTextState } from "@markit/common.types";

type ComplianceMessageTagProps = {
  alternateComplianceMessage?: string;
  alternateContactCardMessage?: string;
  mayBeIncluded?: boolean;
  pastText?: boolean;
};

// Shows a tag for the compliance messaging included in the message, as well as a tag for including the contact card for first-time recipients
const ComplianceMessageTag = (props: ComplianceMessageTagProps) => {
  const {
    alternateComplianceMessage,
    alternateContactCardMessage,
    mayBeIncluded,
    pastText,
  } = props;
  const [showDialog, setShowDialog] = useState(false);
  const [showContactCardDialog, setShowContactCardDialog] = useState(false);
  const { accountData } = useSelector(getAccountState).account;

  const styles = {
    infoIcon: {
      color: Colors.GRAY1,
      marginLeft: 4,
      marginBottom: -3,
      cursor: "pointer",
    },
    dialogContainer: {
      boxShadow: "-1px 1px 4px 0px #1E1E371A",
      backgroundColor: Colors.WHITE,
      zIndex: 99,
      gap: 5,
      width: 400,
      padding: 14,
      borderRadius: 8,
      left: "5%",
      top: hasSubscription(accountData) ? 30 : 30,
    },
  };

  const containerRef = useRef<any>(null);
  useEffect(() => {
    const handler = (event: any) => {
      if (
        showDialog &&
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowDialog(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [showDialog]);

  const contactCardContainerRef = useRef<any>(null);
  useEffect(() => {
    const handler = (event: any) => {
      if (
        showContactCardDialog &&
        contactCardContainerRef.current &&
        !contactCardContainerRef.current.contains(event.target)
      ) {
        setShowContactCardDialog(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [showContactCardDialog]);

  return (
    <div className="ColumnNormal" style={{ gap: 7 }}>
      {/** Compliance Message Tag */}
      <div ref={containerRef} style={{ position: "relative" }}>
        <div style={{ flexWrap: "wrap" }}>
          <span className="bodySubtext">{COMPLIANCE_MESSAGE_DEFAULT}</span>
          <Icon
            icon="mdi:information-outline"
            onClick={() => setShowDialog(!showDialog)}
            height={16}
            style={styles.infoIcon}
          />
        </div>
        {showDialog ? (
          <div
            className="ColumnNormal"
            style={{ ...styles.dialogContainer, position: "absolute" }}
          >
            <span className="bodyMedium">
              Compliance Messaging{mayBeIncluded ? " May Be" : ""} Included
            </span>
            <span style={{ fontSize: 12, color: Colors.GRAY2 }}>
              {alternateComplianceMessage ??
                "If this is the first text someone has been sent, this compliance message must be included."}
            </span>
            {pastText ? (
              <span style={{ fontSize: 12, color: Colors.GRAY2 }}>
                The appropriate number of characters was added to the message
                and may have increased the credit count for these recipients.
              </span>
            ) : (
              <span style={{ fontSize: 12, color: Colors.GRAY2 }}>
                The appropriate number of characters will be added to the
                message and may increase the credit count for these recipients.
              </span>
            )}
          </div>
        ) : null}
      </div>
      {/** Contact Card Tag */}
      {accountData.toggleContactCardFirstText ===
      ContactCardFirstTextState.ENABLED ? (
        <div ref={contactCardContainerRef} style={{ position: "relative" }}>
          <div style={{ flexWrap: "wrap" }}>
            <span className="bodySubtext">
              Contact card added for first-time recipients
            </span>
            <Icon
              icon="mdi:information-outline"
              onClick={() => setShowContactCardDialog(!showContactCardDialog)}
              height={16}
              style={styles.infoIcon}
            />
          </div>
          {showContactCardDialog ? (
            <div
              className="ColumnNormal"
              style={{ ...styles.dialogContainer, position: "absolute" }}
            >
              <span className="bodyMedium">
                Contact card added for first-time recipients
              </span>
              <span style={{ fontSize: 12, color: Colors.GRAY2 }}>
                {alternateContactCardMessage ||
                  "First-time recipients entering this flow will receive a contact card. This requires 3 extra credits per instance."}
              </span>
              <span style={{ fontSize: 12, color: Colors.GRAY2 }}>
                Manage this in your Texting Settings.
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ComplianceMessageTag;
